import type { Json } from "@/integrations/supabase/types";
import { hasAnalyticsConsent } from "@/lib/cookieConsent";

type EnvMap = Record<string, string | boolean | undefined>;

const readEnv = (env: EnvMap, key: string) => {
  const value = env[key];
  return typeof value === "string" && value.trim() !== "" ? value.trim() : undefined;
};

const env = import.meta.env;
const posthogKey = readEnv(env, "VITE_POSTHOG_KEY");
const posthogHost = readEnv(env, "VITE_POSTHOG_HOST") ?? "https://eu.i.posthog.com";

type PostHogClient = typeof import("posthog-js").default;

const BLOCKED_METADATA_PATTERNS = [
  /email/i,
  /clientname/i,
  /projectname/i,
  /prompt/i,
  /budgetcontext/i,
  /pagecontext/i,
  /payload/i,
  /body/i,
  /token/i,
  /amount/i,
  /price/i,
  /total/i,
  /value/i,
];

const isSafePrimitive = (value: Json): value is string | number | boolean | null =>
  value === null || typeof value === "string" || typeof value === "number" || typeof value === "boolean";

const sanitizeAnalyticsMetadata = (metadata: Record<string, Json>) =>
  Object.fromEntries(
    Object.entries(metadata).filter(
      ([key, value]) =>
        !BLOCKED_METADATA_PATTERNS.some((pattern) => pattern.test(key)) &&
        isSafePrimitive(value),
    ),
  );

let posthogClientPromise: Promise<PostHogClient | null> | null = null;

if (typeof window !== "undefined") {
  // Reset the cached client when consent changes so a subsequent
  // capture either initializes (on accept) or keeps refusing to load
  // (on essential_only).
  window.addEventListener("orzio:cookie-consent-change", () => {
    posthogClientPromise = null;
  });
}

const loadPostHog = async (): Promise<PostHogClient | null> => {
  if (!posthogKey || typeof window === "undefined") return null;
  if (!hasAnalyticsConsent()) return null;

  if (!posthogClientPromise) {
    posthogClientPromise = import("posthog-js").then(({ default: posthog }) => {
      if (!posthog.__loaded) {
        posthog.init(posthogKey, {
          api_host: posthogHost,
          capture_pageview: "history_change",
          capture_pageleave: true,
          persistence: "localStorage+cookie",
        });
      }
      return posthog;
    });
  }

  return posthogClientPromise;
};

export const initializeProductAnalytics = async () => {
  await loadPostHog();
};

export const captureProductAnalyticsEvent = async (
  eventName: string,
  metadata: Record<string, Json>,
  user?: { id: string; email?: string } | null,
) => {
  const posthog = await loadPostHog();
  if (!posthog) return;

  if (user?.id) {
    posthog.identify(user.id);
  }

  posthog.capture(eventName, sanitizeAnalyticsMetadata(metadata));
};
